import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";
import DateRangeFilter from "./shared/DateRangeFilter";
import KpiCards from "./shared/KpiCards";
import AnalyticsChart from "./shared/AnalyticsChart";
import AnalyticsMetricList from "./shared/AnalyticsMetricList";
import AnalyticsRankedAxisTick from "./shared/AnalyticsRankedAxisTick";
import AnalyticsWorkspaceHero from "./shared/AnalyticsWorkspaceHero";
import useAnalyticsDashboardData from "./shared/useAnalyticsDashboardData";
import {
  ANALYTICS_SERIES_COLORS,
  buildDistributionSummary,
  DistributionTooltip,
  formatNumber,
  formatPercentage,
  formatRangeLabel,
  getKpiValue,
} from "./shared/analyticsUtils";

function HospitalsSection(props) {
  const [showAll, setShowAll] = useState(false);
  const extraParams = useMemo(() => (showAll ? { limit: "all" } : null), [showAll]);
  const { data, loading, currentStartDate, currentEndDate, handleDateChange } = useAnalyticsDashboardData({
    dataUrl: props.dataUrl,
    startDate: props.startDate,
    endDate: props.endDate,
    extraParams,
  });
  const locale = props.locale || "en";
  const labels = props.labels;
  const topHospitals = data?.top_hospitals || [];
  const totalAppointments = getKpiValue(data?.kpis, "total_appointments");
  const activeHospitals = getKpiValue(data?.kpis, "active_hospitals");
  const totalHospitals = getKpiValue(data?.kpis, "total_hospitals");
  const averagePerHospital = getKpiValue(data?.kpis, "avg_per_hospital");
  const topHospital = topHospitals[0];
  const coverageRate = totalHospitals > 0 ? Math.round((activeHospitals / totalHospitals) * 100) : 0;
  const selectedRangeLabel = formatRangeLabel(currentStartDate, currentEndDate, locale);
  const loadingLabel = labels.loading || "...";

  const heroItems = [
    {
      label: labels.selectedWindow,
      value: selectedRangeLabel || loadingLabel,
    },
    {
      label: labels.totalInWindow,
      value: loading ? loadingLabel : formatNumber(totalAppointments, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.activeHospitals,
      value: loading ? loadingLabel : formatNumber(activeHospitals, locale),
      hint: labels.hospitalsUnit,
    },
    {
      label: labels.topHospital,
      value: loading ? loadingLabel : topHospital?.name || labels.noData,
      hint: topHospital ? `${formatNumber(topHospital.value, locale)} ${labels.appointmentsUnit}` : null,
    },
  ];

  const filterSummaryItems = [
    {
      label: labels.averagePerHospital,
      value: loading ? loadingLabel : formatNumber(averagePerHospital, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.networkCoverage,
      value: loading ? loadingLabel : formatPercentage(coverageRate, locale),
      hint: loading ? null : `${formatNumber(activeHospitals, locale)} / ${formatNumber(totalHospitals, locale)} ${labels.hospitalsUnit}`,
    },
    {
      label: labels.topHospital,
      value: loading ? loadingLabel : topHospital?.name || labels.noData,
      hint: topHospital ? `${formatNumber(topHospital.value, locale)} ${labels.appointmentsUnit}` : null,
    },
  ];

  const spotlightItems = [
    {
      label: labels.topHospital,
      value: topHospital?.name || labels.noData,
      hint: topHospital ? `${formatNumber(topHospital.value, locale)} ${labels.appointmentsUnit}` : null,
    },
    {
      label: labels.averagePerHospital,
      value: formatNumber(averagePerHospital, locale),
      hint: labels.appointmentsUnit,
    },
    {
      label: labels.networkCoverage,
      value: formatPercentage(coverageRate, locale),
      hint: `${formatNumber(activeHospitals, locale)} / ${formatNumber(totalHospitals, locale)} ${labels.hospitalsUnit}`,
    },
  ];

  const rankSummary = buildDistributionSummary(topHospitals, locale, labels.appointmentsUnit, { labelKey: "name" });

  return (
    <div className="analytics-workspace">
      <AnalyticsWorkspaceHero
        eyebrow={labels.heroEyebrow}
        title={labels.heroTitle}
        description={labels.heroDescription}
        items={heroItems}
      />

      <DateRangeFilter
        startDate={currentStartDate}
        endDate={currentEndDate}
        labels={labels}
        onChange={handleDateChange}
        title={labels.filterTitle}
        description={labels.filterDescription}
        rangeLabel={selectedRangeLabel}
        summaryItems={filterSummaryItems}
      />

      <KpiCards kpis={data?.kpis} loading={loading} periodLabel={labels.vsPreviousPeriod} locale={locale} />

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            title={showAll ? labels.allHospitals : labels.topHospitals}
            description={labels.topHospitalsDescription}
            loading={loading}
            isEmpty={!topHospitals.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-hospital"
            toolbar={
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowAll((prev) => !prev)}
                disabled={loading}
              >
                {showAll ? labels.showTopHospitals : labels.showAllHospitals}
              </button>
            }
            aside={<AnalyticsMetricList items={spotlightItems} emptyLabel={labels.noData} />}
            footer={<AnalyticsMetricList items={rankSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={360}
          >
            <ResponsiveContainer width="100%" height={Math.max(360, topHospitals.length * 36)}>
              <BarChart data={topHospitals} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.24)" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" width={180} tick={<AnalyticsRankedAxisTick maxLength={24} />} tickLine={false} axisLine={false} />
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.appointmentsUnit} labelKey="name" />} />
                <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                  {topHospitals.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>
    </div>
  );
}

export default HospitalsSection;
